/**
 * Datamart Type Definitions
 *
 * Types for designing data mart tables (flat ClickHouse tables for BI reports).
 */

// ============================================================================
// Branded Types (IDs)
// ============================================================================

/** Datamart ID (branded type) */
export type TDatamartId = string & { readonly __brand: "DatamartId" };

/** Datamart Column ID (branded type) */
export type TDatamartColumnId = string & { readonly __brand: "DatamartColumnId" };

/** Datamart Folder ID (branded type) */
export type TDatamartFolderId = string & { readonly __brand: "DatamartFolderId" };

// ============================================================================
// Helper Functions
// ============================================================================

export function toDatamartId(id: number | string): TDatamartId {
  return String(id) as TDatamartId;
}

export function toDatamartColumnId(id: number | string): TDatamartColumnId {
  return String(id) as TDatamartColumnId;
}

export function toDatamartFolderId(id: number | string): TDatamartFolderId {
  return String(id) as TDatamartFolderId;
}

// ============================================================================
// Enums
// ============================================================================

/** Supported column data types for datamarts */
export type TDatamartColumnType =
  | "INT"
  | "BIGINT"
  | "FLOAT"
  | "DECIMAL"
  | "VARCHAR"
  | "TEXT"
  | "DATE"
  | "DATETIME"
  | "TIMESTAMP"
  | "BOOLEAN";

/** ClickHouse engine types */
export type TDatamartEngine = "MergeTree" | "ReplacingMergeTree" | "SummingMergeTree" | "AggregatingMergeTree";

// ============================================================================
// Entities
// ============================================================================

/** Datamart folder for grouping datamarts */
export interface IDatamartFolder {
  readonly id: TDatamartFolderId;
  readonly featureId: number;
  readonly name: string;
  readonly parentFolderId?: TDatamartFolderId;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Datamart entity */
export interface IDatamart {
  readonly id: TDatamartId;
  readonly featureId: number;
  readonly folderId?: TDatamartFolderId;
  readonly name: string;
  readonly description?: string;
  readonly engine: TDatamartEngine;
  readonly orderByColumns?: string;
  readonly ttl?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Datamart summary for tree sidebar */
export interface IDatamartSummary {
  readonly id: TDatamartId;
  readonly name: string;
  readonly folderId?: TDatamartFolderId;
  readonly columnCount: number;
}

/** Datamart column */
export interface IDatamartColumn {
  readonly id: TDatamartColumnId;
  readonly datamartId: TDatamartId;
  readonly ordinal: number;
  readonly name: string;
  readonly dataType: TDatamartColumnType;
  /** BM class ID if column is linked to a BM field */
  readonly sourceClassStableId?: string;
  /** BM class name (denormalized for display) */
  readonly sourceClassName?: string;
  /** BM field stable ID if column is linked to a BM field */
  readonly sourceFieldStableId?: string;
  /** BM field name (denormalized for display) */
  readonly sourceFieldName?: string;
  /** Expression / formula (e.g. SUM(amount), COUNT(*), CONCAT(...)) */
  readonly expression?: string;
  /** Whether this column is part of GROUP BY */
  readonly isGroupBy: boolean;
  /** Sort direction (null = no sort) */
  readonly sortDirection?: "ASC" | "DESC";
  /** Sort priority (lower = higher priority) */
  readonly sortOrdinal?: number;
  /** Filter expression */
  readonly filterExpression?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ============================================================================
// Drag & Drop
// ============================================================================

/** Data transferred when dragging a BM field to the datamart panel */
export interface IBmFieldDragData {
  readonly type: "bm-field";
  readonly classStableId: string;
  readonly className: string;
  readonly fieldStableId: string;
  readonly fieldName: string;
  readonly fieldType: string;
}

// ============================================================================
// Requests
// ============================================================================

export interface ICreateDatamartFolderRequest {
  readonly name: string;
  readonly parentFolderId?: TDatamartFolderId;
}

/**
 * Изменение папки: название и/или место в дереве.
 *
 * Норма: datamarts-folders-R09 (переименование), R12 (перенос в другую папку
 * или в корень фичи). Родитель различает три состояния: поле отсутствует —
 * не менять, `null` — корень фичи, идентификатор — эта папка.
 */
export interface IUpdateDatamartFolderRequest {
  readonly name?: string;
  readonly parentFolderId?: TDatamartFolderId | null;
}

export interface ICreateDatamartRequest {
  readonly name: string;
  readonly folderId?: TDatamartFolderId;
  readonly description?: string;
}

export interface IUpdateDatamartRequest {
  readonly name?: string;
  readonly folderId?: TDatamartFolderId | null;
  readonly description?: string | null;
  readonly engine?: TDatamartEngine;
  readonly orderByColumns?: string | null;
  readonly ttl?: string | null;
}

export interface ICreateDatamartColumnRequest {
  readonly name: string;
  readonly dataType: TDatamartColumnType;
  readonly sourceClassStableId?: string;
  readonly sourceClassName?: string;
  readonly sourceFieldStableId?: string;
  readonly sourceFieldName?: string;
  readonly expression?: string;
  readonly isGroupBy?: boolean;
}

export interface IUpdateDatamartColumnRequest {
  readonly name?: string;
  readonly dataType?: TDatamartColumnType;
  readonly sourceClassStableId?: string | null;
  readonly sourceClassName?: string | null;
  readonly sourceFieldStableId?: string | null;
  readonly sourceFieldName?: string | null;
  readonly expression?: string | null;
  readonly isGroupBy?: boolean;
  readonly sortDirection?: "ASC" | "DESC" | null;
  readonly sortOrdinal?: number | null;
  readonly filterExpression?: string | null;
}

export interface IReorderDatamartColumnsRequest {
  readonly columnIds: readonly TDatamartColumnId[];
}
